import type {
  ComponentCharacterCompassSubTrait,
  CharacterCompassPage,
  CharacterCompass,
} from '@edwin-group/cj-graphql/generated/graphql';
import { GENERATED } from '@edwin-group/cj-graphql';
import { completeTask } from '@/services/daily/complete-tasks.service';
import { getCharacterCompass, getPage } from '@/services/daily/character-compass.service';
import { create } from 'zustand';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { sortCharacterCompass } from '../utils';

interface CharacterCompassStore {
  Trait: CharacterCompass | null;
  SubTrait: ComponentCharacterCompassSubTrait | null;
  slices: {
    id: string;
    trait: string | undefined;
    start: string;
    end: string;
  }[];
  traits: CharacterCompass[];
  characterCompassPage: CharacterCompassPage | null;
  loading: boolean;
  onInit: () => Promise<void>;
  onResetAllAnswers: () => void;
  onUpdateTrait: (trait: CharacterCompass | null) => void;
  onUpdateSubTrait: (trait: ComponentCharacterCompassSubTrait | null) => void;
  onUpdateSlices: (
    slices: CharacterCompass[] | ComponentCharacterCompassSubTrait[],
    Trait: CharacterCompass | null,
  ) => void;
}

export const useCharacterCompassStore = create<CharacterCompassStore>()((set, get) => ({
  Trait: null,
  SubTrait: null,
  traits: [],
  slices: [],
  characterCompassPage: null,
  loading: false,
  onInit: async () => {
    set({ loading: true });
    const [traits, page] = await Promise.all([getCharacterCompass(), getPage()]);
    set({ traits: sortCharacterCompass(traits.traits), characterCompassPage: page.page, loading: false });
  },
  onResetAllAnswers: () => {
    const { onUpdateSlices, traits } = get();
    set({ Trait: null, SubTrait: null });
    onUpdateSlices(traits, null);
  },
  onUpdateTrait: (trait: CharacterCompass | null) => {
    set({
      Trait: trait,
    });
  },
  onUpdateSubTrait: async (trait: ComponentCharacterCompassSubTrait | null) => {
    const { Trait } = get();

    const { error } = await completeTask({
      entity: {
        currentTrait: trait,
        activeCharacterCompass: Trait,
        activeCharacterCompassSubTrait: trait,
      },
      type: GENERATED.Enum_Completedtask_Type.CharacterCompass,
    });

    trackGAEvent('character_compass_logged', {
      trait: Trait?.attributes?.trait ?? Trait?.trait ?? '',
      sub_trait: trait?.attributes?.trait ?? trait?.trait ?? '',
    });

    set({
      SubTrait: trait,
    });

    if (error) {
      return;
    }
  },
  onUpdateSlices: (
    slices: CharacterCompass[] | ComponentCharacterCompassSubTrait[],
    Trait: CharacterCompass | null,
  ) => {
    const gradient1From = Trait?.attributes?.typeSection.mainGradientFrom;
    const gradient1To = Trait?.attributes?.typeSection.mainGradientTo;
    const gradient2From = Trait?.attributes?.typeSection.secondaryGradientFrom;
    const gradient2To = Trait?.attributes?.typeSection.secondaryGradientTo;

    if (slices.length > 0) {
      set({
        slices: slices.map((trait, index) => {
          if ('attributes' in trait) {
            return {
              id: `gradient${trait.attributes?.trait}`,
              trait: trait.attributes?.trait,
              start: '#' + (trait.attributes as CharacterCompass)?.typeSection.mainGradientFrom,
              end: '#' + (trait.attributes as CharacterCompass)?.typeSection.mainGradientTo,
            };
          }

          if ('trait' in trait) {
            return {
              id: `gradient${(trait as CharacterCompass)?.trait}`,
              trait: (trait as CharacterCompass)?.trait,
              start: '#' + (index % 2 === 0 ? gradient1From : gradient2From),
              end: '#' + (index % 2 === 0 ? gradient1To : gradient2To),
            };
          }

          return {
            id: '',
            trait: '',
            start: '',
            end: '',
          };
        }),
      });
    }
  },
}));
